import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { TokenService } from '../services/token.service';

/**
 * Ceiling on the token read.
 *
 * The splash now leaves on its own schedule, so whatever this guard is still
 * doing when it goes is what the driver is looking at — an empty router outlet.
 * Preferences.get is a local read that returns in milliseconds; if the bridge
 * ever fails to answer, treat it as signed out and show the login screen rather
 * than hold the first navigation open behind a splash that has already left.
 */
const TOKEN_READ_CAP_MS = 2_000;

/**
 * Decides the first screen before the first navigation happens.
 *
 * The '' route used to redirect straight to /login, and AppComponent corrected
 * it to /home afterwards, once its own startup chain had read the token. That
 * was invisible while the splash sat over the whole of startup. The splash no
 * longer waits for startup, so the correction would now show as a flash of the
 * login screen on every launch by a signed-in driver.
 *
 * Reading the token is a Preferences lookup and TokenService.init() is
 * idempotent, so resolving it here costs nothing that was not already paid.
 */
export const startupRedirectGuard: CanActivateFn = async () => {
  const tokens = inject(TokenService);
  const router = inject(Router);
  try {
    await Promise.race([
      tokens.init(),
      new Promise<void>(resolve => setTimeout(resolve, TOKEN_READ_CAP_MS)),
    ]);
  } catch {
    /* storage unreadable — treat as signed out rather than blocking the launch */
  }
  return router.parseUrl(tokens.getToken() ? '/home' : '/login');
};
